/**
 * Safe-area insets: the strip along each edge the OS owns — status bar, navigation bar,
 * cutout, home indicator.
 *
 * The four `--safe-*` properties are declared in `index.css` from `env(safe-area-inset-*)`
 * and overwritten on Android by `MainActivity` with measured `WindowInsets`, because
 * Android's WebView reports only display cutouts through `env()` — so the raw nav-bar value
 * there is always 0px and anything relying on it gets covered.
 *
 * Rules of thumb:
 *  - Anything fixed/absolute against a screen edge pads with the matching helper.
 *  - Scroll containers in a bottom-pinned sheet pad their CONTENT bottom, so the last row
 *    scrolls clear of the nav bar instead of sitting under it.
 *  - Never write `env(safe-area-inset-*)` directly; it silently means "0 on Android".
 *
 * Values are plain CSS strings, usable in any inline style.
 */

const pad = (varName: string, extraPx: number) =>
  extraPx === 0 ? `var(${varName}, 0px)` : `calc(var(${varName}, 0px) + ${extraPx}px)`

const inset = (side: 'top' | 'right' | 'bottom' | 'left', extraPx: number) =>
  pad(`--safe-${side}`, extraPx)

/** Space owned by the status bar / notch, plus `extraPx` of your own padding. */
export const safeTop = (extraPx = 0) => inset('top', extraPx)

/** Space owned by the navigation bar / home indicator, plus `extraPx` of your own padding. */
export const safeBottom = (extraPx = 0) => inset('bottom', extraPx)

/**
 * Only the part of the bottom inset the system takes TAPS in: a 3-button nav bar's height,
 * and 0 under gesture navigation, where the handle is drawn over the app but taps pass
 * through.
 *
 * Use this instead of `safeBottom` for a surface sitting flush against the bottom of the
 * screen, as the tab bar does. Everything else still wants `safeBottom`, so it clears the
 * gesture handle rather than being drawn under it.
 */
export const safeBottomButtons = (extraPx = 0) => pad('--safe-bottom-buttons', extraPx)

/**
 * How much of the bottom of the viewport the on-screen keyboard covers, 0 when it is
 * closed. Deliberately NOT one of the safe-area insets: it is transient, it overlaps the
 * navigation bar rather than adding to it, and only a surface holding a text input has
 * any business reading it. See `lib/keyboardInset.ts` and the `--keyboard-inset` comment
 * in index.css for who writes it.
 */
export const keyboardInset = (extraPx = 0) => pad('--keyboard-inset', extraPx)

/** Landscape cutout / gesture edges. */
export const safeLeft = (extraPx = 0) => inset('left', extraPx)
export const safeRight = (extraPx = 0) => inset('right', extraPx)
